import React, { useCallback, useMemo, useState } from 'react';
import * as Styles from './AiChatSurvey.styles';
import { Button, IconButton, TextField } from 'gatorcomponents';
import { STEPS } from './AiChatSurvey.types';
import useLocale from '@/hooks/useLocale';

const AiChatSurvey = ({
  fullScreen,
  handleRateChat,
  handleClose,
  isAnswered,
  setIsAnswered,
}) => {
  const [step, setStep] = useState(isAnswered ? STEPS.FINISHED : STEPS.SCORE);
  const [selectedScore, setSelectedScore] = useState(null);
  const [feedbackMessage, setFeedbackMessage] = useState('');

  const { aichat: aichatLocale } = useLocale();
  const { surveyLocale } = aichatLocale;

  const scores = useMemo(() => [{
    value: 1, label: '😡', ariaLabel: '1',
  },
  {
    value: 2, label: '😢', ariaLabel: '2',
  },
  {
    value: 3, label: '😐', ariaLabel: '3',
  },
  {
    value: 4, label: '😍', ariaLabel: '4',
  },
  {
    value: 5, label: '🥰', ariaLabel: '5',
  }], []);

  const onContinue = useCallback(() => {
    handleRateChat && handleRateChat({
      rating: selectedScore,
      message: feedbackMessage,
    });
    setStep(step === STEPS.SCORE ? STEPS.FEEDBACK : STEPS.FINISHED);
  }, [handleRateChat, selectedScore, feedbackMessage, step]);

  const scoreStep = useCallback(() => (
    <>
      <Styles.ScoreWrapper data-testid="score-step-wrapper">
        {scores.map((item, index) => (
          <Styles.ScoreButton
            key={item.label}
            role="button"
            tabIndex={index}
            aria-label={`${item.ariaLabel}`}
            onClick={() => setSelectedScore(item.value)}
            $selected={selectedScore === item.value}
            data-testid={`score-step-item-${item.value}`}
          >
            {item.label}
          </Styles.ScoreButton>
        ))}

      </Styles.ScoreWrapper>

      <Styles.ScoreLabelRow>
        <Styles.ScoreLabel>{surveyLocale.emojiBad}</Styles.ScoreLabel>
        <Styles.ScoreLabel>{surveyLocale.emojiGood}</Styles.ScoreLabel>
      </Styles.ScoreLabelRow>
    </>
  ), [scores, selectedScore, surveyLocale]);

  const feedbackStep = useCallback(() => (
    <Styles.FeedbackWrapper>
      <TextField
        type="textarea"
        label={surveyLocale.textareaPlaceholder}
        onChange={e => setFeedbackMessage(e.target.value)}
        value={feedbackMessage}
        maxLength={200}
        autoFocus
        testId="textarea"
      />
    </Styles.FeedbackWrapper>
  ), [feedbackMessage, surveyLocale]);

  const renderCurrentStep = useCallback(() => {
    const rules = {
      [STEPS.SCORE]: () => scoreStep(),
      [STEPS.FEEDBACK]: () => feedbackStep(),
      [STEPS.FINISHED]: () => <></>,
    };

    return rules[step]();
  }, [step, scoreStep, feedbackStep]);

  const renderTitle = () => {
    const rules = {
      [STEPS.SCORE]: surveyLocale.titleScore,
      [STEPS.FEEDBACK]: surveyLocale.titleFeedback,
      [STEPS.FINISHED]: surveyLocale.titleFinished,
    };

    return rules[step];
  };

  const onClose = () => {
    handleClose && handleClose();
  };

  const isDisabled = () => {
    if (step === STEPS.SCORE) {
      return !selectedScore;
    }

    if (step === STEPS.FEEDBACK) {
      return !feedbackMessage;
    }
  };

  const handleContinue = () => {
    if (step === STEPS.FINISHED) {
      setIsAnswered(true);
      onClose();
    }

    if (step === STEPS.FEEDBACK) {
      setIsAnswered(true);
    }

    onContinue();
  };

  return (
    <Styles.Wrapper $fullScreen={fullScreen}>
      <Styles.TitleRow>
        <Styles.Title $fullScreen={fullScreen} data-testid="survey-title">
          {renderTitle()}
        </Styles.Title>
        <IconButton
          icon="IconClose"
          variant="clear"
          size="md"
          onClick={onClose}
        />
      </Styles.TitleRow>

      {renderCurrentStep()}

      <Styles.ActionRow>
        <Button
          label={step === STEPS.FINISHED ? surveyLocale.closeCta : surveyLocale.continueCta}
          variant="primary"
          size="medium2"
          disabled={isDisabled()}
          onClick={handleContinue}
          testId="button"
        />
      </Styles.ActionRow>
    </Styles.Wrapper>
  );
};

export default AiChatSurvey;
